import { Award, BatteryCharging, BadgeCheck, House, ShieldCheck, Zap } from "lucide-react";

import teslaLogo from "@/assets/partners/tesla.webp.asset.json";
import enphaseLogo from "@/assets/partners/enphase.webp.asset.json";
import qcellsLogo from "@/assets/partners/qcells.webp.asset.json";
import duracellLogo from "@/assets/partners/duracell.webp.asset.json";

const manufacturers: { name: string; src?: string }[] = [
  { name: "Owens Corning" },
  { name: "IKO" },
  { name: "Elevate" },
  { name: "Enphase", src: enphaseLogo.url },
  { name: "SolarEdge" },
  { name: "Tesla", src: teslaLogo.url },
  { name: "SunPower" },
  { name: "Qcells", src: qcellsLogo.url },
  { name: "Duracell", src: duracellLogo.url },
  { name: "GAF" },
];

/**
 * Certifications and manufacturer logo band.
 * Shared between the About page and the homepage.
 */
export function CredentialsSection({
  className = "bg-muted/40",
  showFeaturedCerts = false,
}: {
  className?: string;
  showFeaturedCerts?: boolean;
}) {
  return (
    <section className={className}>
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        {showFeaturedCerts && (
          <div>
            <div className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-secondary">
                <Award className="h-4 w-4" /> Proven expertise
              </div>
              <h2 className="mt-3 text-3xl font-extrabold text-primary sm:text-4xl text-balance">Certified where it counts</h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                One license, three trades, and manufacturer certifications that help protect your roof, solar, and battery investment.
              </p>
            </div>

            <div className="mt-10 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
              {featuredCredentials.map(({ title, description, icon: Icon, label }) => (
                <article
                  key={title}
                  className="group relative overflow-hidden rounded-lg border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-elegant"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider text-secondary">{label}</span>
                  </div>
                  <h3 className="mt-6 text-xl font-bold leading-snug text-primary">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground">{description}</p>
                </article>
              ))}
            </div>

            <p className="mx-auto mt-7 max-w-5xl text-center text-xs leading-relaxed text-muted-foreground">
              Manufacturer certifications and license classifications are subject to their respective program requirements. Verify current license information with the California Contractors State License Board.
            </p>
          </div>
        )}

        <div className={showFeaturedCerts ? "mt-14" : ""}>
          <h3 className="text-2xl font-extrabold text-primary">Manufacturer systems we install</h3>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Our roofing and solar systems are built with components from these manufacturers.
          </p>
          <div className="mt-8 grid grid-cols-2 items-center gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {manufacturers.map((m) => (
              <div key={m.name} className="flex min-h-16 items-center justify-center rounded-2xl border border-border bg-card px-4 py-3 text-center">
                {m.src ? (
                  <img
                    decoding="async"
                    loading="lazy"
                    width={140}
                    height={40}
                    src={m.src}
                    alt={`${m.name} logo`}
                    className="h-8 w-auto max-w-[140px] object-contain opacity-60 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0 sm:h-10"
                  />
                ) : (
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary/70">{m.name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const featuredCredentials = [
  {
    title: "GAF Certified™ Contractor",
    description: "Residential and commercial roofing backed by manufacturer-certified installation standards and eligible GAF warranty options.",
    icon: House,
    label: "Roofing",
  },
  {
    title: "Tesla Certified Installer",
    description: "Powerwall battery storage installed by the same experienced team that plans and protects your roofing system.",
    icon: BatteryCharging,
    label: "Energy storage",
  },
  {
    title: "Enphase Certified Installer",
    description: "IQ Battery systems designed around your property, energy use, and long-term backup power goals.",
    icon: Zap,
    label: "Solar + battery",
  },
  {
    title: "CSLB #1130793 · B / C-10 / C-39",
    description: "Roofing, electrical, and general building under one California license, helping keep your project coordinated from start to finish.",
    icon: ShieldCheck,
    label: "Licensed contractor",
  },
  {
    title: "NRCA Member",
    description: "National Roofing Contractors Association member, staying current on codes, safety, and roofing best practices.",
    icon: BadgeCheck,
    label: "Industry member",
  },
];


